import { daysOptions, studentClasses } from "@data/constants/formConstants"
import * as WebBrowser from "expo-web-browser"
import moment from "moment"
import "moment/locale/fr"
import { IS_WEB } from "../../common/utils/mobileUtils"
import {
    congratsList,
    stepErrorList,
    stepFinishedList,
} from "../constants/activitiesConstants"

// Helper function to parse subject_ids from different formats to array
export function parseSubjectIds(subjectIds) {
    if (!subjectIds) return []

    // If it's already an array, return it
    if (Array.isArray(subjectIds)) return subjectIds

    // If it's a string, try to parse it as JSON
    if (typeof subjectIds === "string") {
        try {
            const parsed = JSON.parse(subjectIds)
            return Array.isArray(parsed) ? parsed : [parsed]
        } catch (error) {
            console.warn("Failed to parse subject_ids:", subjectIds, error)
            return []
        }
    }

    // If it's a number, wrap it in an array
    if (typeof subjectIds === "number") return [subjectIds]

    return []
}

// compare app versions
export function versionCompare(v1, v2, options) {
    if (!v1 || !v2) return NaN
    var lexicographical = options && options.lexicographical,
        zeroExtend = options && options.zeroExtend,
        v1parts = v1.split("."),
        v2parts = v2.split(".")

    function isValidPart(x) {
        return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x)
    }

    if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
        return NaN
    }

    if (zeroExtend) {
        while (v1parts.length < v2parts.length) v1parts.push("0")
        while (v2parts.length < v1parts.length) v2parts.push("0")
    }

    if (!lexicographical) {
        v1parts = v1parts.map(Number)
        v2parts = v2parts.map(Number)
    }

    for (var i = 0; i < v1parts.length; ++i) {
        if (v2parts.length == i) {
            return 1
        }

        if (v1parts[i] == v2parts[i]) {
            continue
        } else if (v1parts[i] > v2parts[i]) {
            return 1
        } else {
            return -1
        }
    }

    if (v1parts.length != v2parts.length) {
        return -1
    }

    return 0
}

// ** Random int in interval
export function randomIntFromInterval(min, max) {
    // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min)
}

// ** Gets unique items for a string array
export const uniq = (a) => {
    if (!a) return null

    return a.sort().filter(function (item, pos, ary) {
        return !pos || item !== ary[pos - 1]
    })
}

// ** Gets unique items for an array on for key
export const uniqWithKey = (a, key) => {
    if (!a) return null

    return a.sort().filter(function (item, pos, ary) {
        return !pos || item[key] !== ary[pos - 1][key]
    })
}

// ** Format duration in seconds to minutes string (e.g., "5min", "<1min", "0min")
export const formatDurationMinutes = (seconds, showZero = true) => {
    if (!seconds || seconds === 0) return showZero ? "0min" : null
    const minutes = Math.round(seconds / 60)
    return minutes > 0 ? `${minutes}min` : "<1min"
}

// ** Check what type of object it is
export const whatIsIt = (object) => {
    const stringConstructor = "test".constructor
    const arrayConstructor = [].constructor
    const objectConstructor = {}.constructor

    if (object === null) {
        return "null"
    }
    if (object === undefined) {
        return "undefined"
    }
    if (object.constructor === stringConstructor) {
        return "String"
    }
    if (object.constructor === arrayConstructor) {
        return "Array"
    }
    if (object.constructor === objectConstructor) {
        return "Object"
    }
    return "Other"
}

//find the key of a given value
export function findKeyByValue(obj, value) {
    for (let key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] === value) {
            return key
        }
    }
    return null // Key not found for the given value
}

// ** Updates data keys smartly (without removing keys that are not in newObject)
export const updateDataKeys = (newObject, previousObject) => {
    const returnedObject = { ...previousObject }

    Object.keys(newObject).forEach((key) => {
        // console.log(`entered loop with key : ${key}`)

        if (whatIsIt(newObject[key]) === "Array") {
            // console.log(`key : ${key} is array`)

            returnedObject[key] = []

            newObject[key].forEach((newItem) => {
                let returnedItem

                if (
                    whatIsIt(newObject[key]) !== "Array" &&
                    whatIsIt(newItem) === "Object"
                ) {
                    const prevItem = previousObject[key]?.find(
                        (x) => x.id === newItem.id
                    )
                    if (prevItem)
                        returnedItem = updateDataKeys(newItem, prevItem)
                    else returnedItem = newItem
                } else {
                    returnedItem = newItem
                }

                returnedObject[key].push(returnedItem)
            })
        } else if (whatIsIt(newObject[key]) === "Object") {
            // console.log(`key : ${key} is object`)
            returnedObject[key] = updateDataKeys(
                newObject[key],
                previousObject?.[key]
            )
        } else {
            // console.log(`key : ${key} is other`)
            returnedObject[key] = newObject[key]
        }
    })

    return returnedObject
}

// ** Average of array
export const calculateAverage = (a) => {
    if (a === null) return 0
    if (a?.length === 0) return 0

    let sum = 0
    for (let i = 0; i < a?.length; i++) {
        sum += parseInt(a[i], 10) //don't forget to add the base
    }

    return Math.round((sum / a?.length) * 10) / 10
}

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = (num) =>
    num > 999 ? `${(num / 1000).toFixed(1)}k` : num

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "")

// ** Checks if the passed date is today
const isToday = (date) => {
    const today = new Date()
    return (
        /* eslint-disable operator-linebreak */
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
        /* eslint-enable */
    )
}

// ** String truncation
export const truncateString = (str, num) => {
    // If the length of str is less than or equal to num
    // just return str--don't truncate it.
    if (str.length <= num) {
        return str
    }
    // Return str truncated with '...' concatenated to the end of str.
    return `${str.slice(0, num)}...`
}

// ** Capitalize
export const capitalize = (s) => {
    if (typeof s !== "string") return ""
    return s?.charAt(0).toUpperCase() + s?.slice(1)
}

// ** Return date options in period for Challenge form
export const dateOptionsInPeriod = (min, max) => {
    const currentMoment = moment().add(min, "days")

    const dateArray = []

    for (let i = min; i < max; i++) {
        currentMoment.add(1, "day")
        dateArray.push({
            value: currentMoment.format("YYYY-MM-DD"),
            label: "Dans " + (i + 1) + " jour" + (i > 0 ? "s" : ""),
            description: capitalize(currentMoment.format("ddd DD MMMM")),
        })
    }

    return dateArray
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String|Moment} value date to format
 */
export const formatDate = (value) => {
    if (!value) return "-"

    if (typeof value === "string") return moment(value).format("DD MMMM YYYY")

    return value.format("DD MMMM YYYY")
    // return new Intl.DateTimeFormat('fr-FR', formatting).format(new Date(value))
}

export const formatDateNumeric = (value) => {
    if (!value) return "-"

    if (typeof value === "string") return moment(value).format("DD/MM/YYYY")

    return value.format("DD/MM/YYYY")
}

// format minutes to hours and minutes
export const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    if (hours === 0) {
        return `${remainingMinutes} min`
    } else if (remainingMinutes === 0) {
        return `${hours}h`
    } else {
        return `${hours}h${padToTwo(remainingMinutes)}`
    }
}
// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
    if (!value) return null
    const date = new Date(value)
    let formatting = { month: "short", day: "numeric", weekday: "long" }

    if (toTimeForCurrentDay && isToday(date)) {
        formatting = { hour: "numeric", minute: "numeric" }
    }

    return new Intl.DateTimeFormat("fr-FR", formatting).format(date)
}

// ** Format relativeDate
export const formatRelativeDateTime = (value, dayMode = false) => {
    moment.locale("fr")
    // const now = dayMode ? moment().startOf('day') : moment()
    const now = moment()
    // const date = dayMode ? moment(value).startOf('day') : moment(value)
    const date = moment(value)
    const diff = dayMode ? now.diff(date, "days") : now.diff(date, "hours")

    if (dayMode) {
        return date.fromNow()
        // else if (diff === -1) return "Demain"
        // else if (diff === 1) return "Hier"
        // else return "Aujourd'hui"
    } else {
        if (diff < 22) return date.fromNow()
        else return date.calendar()
    }
}

// ** Format as calendar day
export const formatCalendarMoment = (value, withTime = false) => {
    const withTimeAddendum = withTime ? " [à] HH:mm" : ""

    moment.locale("fr")

    return capitalize(
        moment(value).calendar(null, {
            sameDay: "HH:mm",
            nextDay: "[Demain]",
            nextWeek: "dddd [prochain]",
            lastDay: "[Hier]" + withTimeAddendum,
            lastWeek: "dddd [dernier]" + withTimeAddendum,
            sameElse: "dddd DD/MM" + withTimeAddendum,
        })
    )
}

// ** Format full date time
export const formatFullDateTime = (value) => {
    moment.locale("fr")
    const date = moment(value)
    const dayFormat = date.isSame(moment(), "day")
        ? "[aujourd'hui à]"
        : "dddd D MMMM [à]"

    return capitalize(date.format(`${dayFormat} HH:mm`))
}

export const formatFullDateTimeNumeric = (value) => {
    const date = moment(value)
    const now = moment()

    if (date.isSame(now, "day")) {
        return `aujourd'hui à ${date.format("HH:mm")}`
    } else if (date.isSame(now.subtract(1, "days"), "day")) {
        return `hier à ${date.format("HH:mm")}`
    } else {
        return date.format("DD/MM/YYYY à HH:mm")
    }
}

// ** Format full date time
export const formatTime = (value) => {
    moment.locale("fr")
    const date = moment(value)

    return date.format("HH:mm")
}

// ** get week day from date
export const weekDayFromDate = (value) => {
    const date = moment(value)

    return date.locale("en").format("dddd")
}

// Add utility to compute first matching weekday date
export function computeFirstMatchingDate(originalDate, dayBlocks) {
    const dayNames = dayBlocks.map((db) => db.day)
    if (!originalDate || dayNames.length === 0) return originalDate
    const dateObj = new Date(originalDate)
    for (let i = 0; i < 7; i++) {
        const d = new Date(dateObj)
        d.setDate(dateObj.getDate() + i)
        const weekday = d.toLocaleDateString("en-US", { weekday: "long" })
        if (dayNames.includes(weekday)) {
            return d.toISOString().split("T")[0]
        }
    }
    return originalDate
}

// ** check if date is passed
export const isPassedDate = (value) => {
    const date = moment(value)

    return date.diff(moment()) <= 0
}

// ** check if we are in the weekend
export const isItWeekend = () => {
    const todayWeekDay = moment().format("dddd")
    return todayWeekDay === "samedi" || todayWeekDay === "dimanche"
}

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary25: "#ffc80a1a", // for option hover bg-color
        primary: "#ffc80a", // for selected option bg-color
        neutral10: "#ffc80a", // for tags bg-color
        neutral20: "#ededed", // for input border-color
        neutral30: "#ededed", // for input hover border-color
    },
})

// ** Return random congrats message
export const congratsMessage = (task) => {
    const num = task?.id % congratsList.length
    return congratsList[num] ?? "Terminé"
}

export const stepFinishedMessage = (index) => {
    const num = index % stepFinishedList.length
    return stepFinishedList[num] ?? "Bravo !"
}

export const stepErrorMessage = (index) => {
    const num = index % stepErrorList.length
    return stepErrorList[num] ?? "Raté !"
}

// ** Pad to two digits for timer
export const padToTwo = (number) =>
    number <= 9 && number?.toString()?.length < 2 ? `0${number}` : number
export const displayTimeSecondsNoPad = (seconds) => {
    let minutes = 0
    if (seconds < 0) {
        seconds = 0
    }

    if (seconds < 60) {
        // return `00:${padToTwo(seconds)}:${(remainDeciseconds)}`;
        return `0:${seconds}`
    }
    let remainSeconds = seconds % 60
    minutes = (seconds - remainSeconds) / 60
    // return `${padToTwo(minutes)}:${padToTwo(remainSeconds)}:${(remainDeciseconds)}`;
    return `${minutes}:${remainSeconds}`
}

export const hoursMinutesAndSecondsFromSeconds = (seconds) => {
    const duration = moment.duration(seconds, "seconds")
    const hours = duration.hours()
    const minutes = duration.minutes()
    const secondsLeft = duration.seconds()
    return [hours, minutes, secondsLeft]
}

// TODO: duplicated??
export const formatSeconds = (seconds) => {
    return {
        hours: Math.floor(seconds / 3600),
        minutes: Math.floor((seconds % 3600) / 60),
        seconds: seconds % 60,
    }
}

export const formatDaysHoursMinutesSeconds = (seconds) => {
    return {
        days: Math.floor(seconds / 86400),
        hours: Math.floor((seconds % 86400) / 3600),
        minutes: Math.floor((seconds % 3600) / 60),
        seconds: seconds % 60,
    }
}

// ** Display time based on seconds for timer
export const displayTimeSeconds = (seconds) => {
    const sign = seconds < 0 ? "-" : ""
    const absSeconds = Math.abs(seconds)
    const minutes = Math.floor(absSeconds / 60)
    const remainSeconds = absSeconds % 60
    return `${sign}${padToTwo(minutes)}:${padToTwo(remainSeconds)}`
}

export const toTimeString = (totalSeconds) => {
    const totalMs = totalSeconds * 1000
    const result = new Date(totalMs).toISOString().slice(11, 19)

    return result
}

// ** Make copy of time from object
export const copyObjectWithCurrentTimeAttribute = (attribute, obj) => {
    const copy = Object.assign({}, obj)
    copy[attribute] = moment().format("YYYY-MM-DD HH:mm:ss")
    // console.log(copy)
    return copy
}

// ** Add timestamp to useForm field
export const addTimestampToField = (field, step, action, stepData = null) => {
    // console.log("step data passed ", stepData)

    field?.onChange([
        ...field?.value?.filter(
            (v) => v?.action !== action || v?.step !== step
        ),
        {
            action: action,
            step: step,
            created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
            step_data: stepData,
        },
    ])

    // console.log("timestamp ", field.value)
}

// ** Add form data to useForm field
export const addFormDataToField = (
    field,
    step,
    formData,
    action = "step_form_sent"
) => {
    // console.log(field, step, formData, action)

    field?.onChange([
        ...field?.value?.filter(
            (v) => v?.action !== action || v?.step !== step
        ),
        {
            action: action,
            step: step,
            created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
            step_data: formData,
        },
        {
            action: "step_finished",
            step: step,
            created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
            // step_data: null
        },
    ])

    // console.log(field.value)
}

// ** Add work attachment to useForm field
export const addWorkAttachmentToField = (
    field,
    step,
    media = [],
    userId,
    type = "jpg",
    action = "step_media_sent"
) => {
    // clean existing images
    let newFieldValue = [...field?.value]?.filter(
        (x) => x?.step !== step || x?.action !== action
    )

    media.forEach((x) => {
        newFieldValue = [
            ...newFieldValue,
            {
                action: action,
                step: step,
                work_attachment: {
                    name: userId + "_wa_" + moment().format() + "." + type,
                    type: type === "jpg" ? "image/jpg" : "audio/m4a",
                    // uri: Platform.OS === 'ios' ? x.uri.replace('file://', '') : x.uri,
                    uri: x?.uri,
                    width: x?.width,
                    height: x?.height,
                },
                created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
            },
        ]

        // console.log("media ", field.value)
    })

    field?.onChange(newFieldValue)
}

// ** Add media to useForm field
export const addMediaToField = (
    field,
    media = [],
    overwrite = false,
    prefix = null,
    type = "png"
) => {
    // let newFieldValue = overwrite ? {file_keys: []} : {...field?.value}
    //
    // media.forEach(x => {
    //
    //     const newKey = field?.name + "_" + newFieldValue?.file_keys?.length
    //
    //     newFieldValue = {
    //         ...newFieldValue,
    //         file_keys: [...newFieldValue?.file_keys, newKey]
    //     }
    //     newFieldValue[newKey] = {
    //         name: (prefix ?? newKey) + "_" + moment().format() + "." + type,
    //         type: type === "jpg" ? "image/jpg" : "audio/m4a",
    //         height: x?.height,
    //         width: x?.width,
    //         uri: x?.uri
    //     }
    // })

    field?.onChange(
        media?.map((file) => {
            return {
                type: type === "png" ? "image/png" : "audio/m4a",
                height: file?.height,
                width: file?.width,
                uri: file?.uri,
            }
        })
    )
}

//display time mm:ss
export function secondsToHMS(seconds) {
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    const mm = minutes < 10 ? `0${minutes}` : minutes
    const ss = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds

    return `${mm} min ${ss} s`
}

// ** Display time nicely
export function niceTimeFromSeconds(duration) {
    if (duration < 3600)
        return [
            Math.floor(duration / 60),
            plr("minute", Math.floor(duration / 60)),
        ]
    else {
        const hours = Math.floor(duration / 3600)
        const minutes =
            Math.floor((duration - hours * 3600) / 60) > 0
                ? Math.floor((duration - hours * 3600) / 60)
                : ""
        return [duration ? `${hours}h${minutes}` : "0", "temps"]
    }
}

// ** Get last item of an array
export function lastItem(mArray) {
    return mArray?.[mArray?.length - 1]
}

export function createArray(start, end, step = 1) {
    return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index * step
    )
}

// ** Get path to array
function leaf(obj, path) {
    path = path.split(".")
    let res = obj
    for (let i = 0; i < path.length; i++) res = res?.[path[i]]
    return res
}

// ** Check arrays items equality at path
export const areArraysEqualAtPath = (arrayA, arrayB, pathA, pathB) => {
    arrayA?.forEach((itemA, index) => {
        if (leaf(itemA, pathA) !== leaf(arrayB?.[index], pathB)) return false
    })
    return true
}

// ** Check arrays items equality
export const isSameArray = (arrayA, arrayB) => {
    return arrayA?.sort().join(",") === arrayB?.sort().join(",")
}

export const allOptionsFromSectionOptions = (options) =>
    options
        ?.map((option) => option?.data ?? option)
        ?.flat()
        ?.map((d) => d?.value)

// ** Pluralize string based on count
export const plr = (textString, cnt, character = "s") =>
    cnt > 1 ? textString + character : textString

export const readableDayTime = (
    day_time,
    durationInMinutes = 60,
    roomType = "biweekly"
) => {
    const dayMatch = day_time?.split(" ")?.[0]
    const readableDay = daysOptions
        ?.find((d) => d.value === dayMatch)
        ?.label?.toLowerCase()

    const additionalHours = Math.floor(durationInMinutes / 60)
    const additionalMinutes = durationInMinutes % 60

    const timeMatch = day_time?.split(" ")?.[1]
    const timeMatchHour = timeMatch?.substring(0, 2)
    const timeMatchMinutes = timeMatch?.substring(3, 5)
    const readableMinutes = timeMatchMinutes === "00" ? "" : timeMatchMinutes

    const readableEndHours = parseInt(timeMatchHour) + additionalHours
    const readableEndMinutes =
        parseInt(timeMatchMinutes) + additionalMinutes === 0
            ? ""
            : parseInt(timeMatchMinutes) + additionalMinutes

    const additionalHoursPlus = Math.floor(readableEndMinutes / 60)
    const additionalMinutesPlus = readableEndMinutes % 60

    const finalEndHours = readableEndHours + additionalHoursPlus
    const finalEndMinutes =
        additionalMinutesPlus === 0 ? "" : additionalMinutesPlus

    const readableTime =
        parseInt(timeMatchHour) +
        "h" +
        readableMinutes +
        "-" +
        finalEndHours +
        "h" +
        finalEndMinutes

    // if (roomType === "holiday_course")
    return capitalize(readableDay) + " " + readableTime

    // return "Tous les " + readableDay + "s " + readableTime
}

// Format a moment time as "18h" or "18h30" (drop "00" minutes)
const formatTimeShort = (m) => {
    const min = m.minutes()
    return min === 0 ? `${m.hours()}h` : `${m.hours()}h${padToTwo(min)}`
}

/**
 * Build per-session display strings by combining live_sessions (timezone-aware day_time)
 * with the static class/subject map from school_subjects_classes_per_session.
 *
 * @param {Array} liveSessions - Array of live session objects (with day_time already localized)
 * @param {Object} perSessionMap - Dictionary { live_session_id: "class_subject_string" }
 * @returns {Array} Array of formatted strings, e.g. ["Lundi 18h-19h : 3ème Maths|Physique"]
 */
export const buildPerSessionDisplayStrings = (liveSessions, perSessionMap) => {
    if (!liveSessions || !perSessionMap) return []

    return liveSessions
        .filter((session) => perSessionMap[session.id])
        .map((session) => {
            const dayTime = moment(session?.day_time, "dddd HH:mm", "en").locale("fr")
            const day = capitalize(dayTime.format("dddd"))
            const startTime = formatTimeShort(dayTime)
            const endTime = formatTimeShort(
                moment(dayTime).add(session.duration, "minutes")
            )

            const classSubject = perSessionMap[session.id]
            return `${day} ${startTime}-${endTime} : ${classSubject}`
        })
}

/**
 * Sort live sessions by day and time chronologically
 * @param {Array} liveSessions - Array of live session objects
 * @returns {Array} Sorted array of live sessions
 */
export const sortLiveSessionsByDayTime = (liveSessions) => {
    if (!liveSessions || !Array.isArray(liveSessions)) return []

    const dayOrder = {
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
        Sunday: 7,
    }

    return [...liveSessions].sort((a, b) => {
        // Parse day_time format: "Tuesday 16:25" or use day + time separately
        const [dayA, timeA] = (a.day_time || a.day + " " + a.time).split(" ")
        const [dayB, timeB] = (b.day_time || b.day + " " + b.time).split(" ")

        // Compare by day first
        const dayComparison = (dayOrder[dayA] || 0) - (dayOrder[dayB] || 0)
        if (dayComparison !== 0) return dayComparison

        // If same day, compare by time
        const [hoursA, minutesA] = timeA.split(":").map(Number)
        const [hoursB, minutesB] = timeB.split(":").map(Number)
        const timeInMinutesA = hoursA * 60 + minutesA
        const timeInMinutesB = hoursB * 60 + minutesB

        return timeInMinutesA - timeInMinutesB
    })
}

export const mapRoomTypeToOffer = (roomType) => {
    const mapping = {
        biweekly: "Cours Partagés",
        individual: "Individuel",
        study_group: "Étude encadrée",
    }
    return mapping[roomType] || roomType
}

export function string_to_slug(str) {
    str = str.replace(/^\s+|\s+$/g, "") // trim
    str = str.toLowerCase()

    // remove accents, swap ñ for n, etc
    var from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;"
    var to = "aaaaeeeeiiiioooouuuunc------"
    for (var i = 0, l = from.length; i < l; i++) {
        str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i))
    }

    str = str
        .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
        .replace(/\s+/g, "-") // collapse whitespace and replace by -
        .replace(/-+/g, "-") // collapse dashes

    return str
}

export function isArraySubset(mainArray, subArray) {
    if (!Array.isArray(mainArray) || !Array.isArray(subArray)) {
        return false
    }

    return subArray.every((element) => mainArray.includes(element))
}

export const formatOccurrenceDate = (date) =>
    capitalize(moment(date)?.format("dddd" + " DD MMMM [à] HH:mm"))

/**
 * Checks if a live room has a specific feature based on its type
 * @param {Object} liveRoom - The live room object to check
 * @param {String} featureLabel - The feature label to check for
 * @returns {Boolean} - true if the feature is supported, false otherwise
 */
export const hasFeature = (liveRoom, featureLabel) => {
    if (!liveRoom || !liveRoom.version_ft || !featureLabel) return false
    return liveRoom.version_ft.includes(featureLabel)
}

// ** Color utility functions for creating variations
export const hexToRgb = (hex) => {
    let c = hex.startsWith("#") ? hex.slice(1) : hex
    if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2]
    const num = parseInt(c, 16)
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}

export const rgbToHsl = ({ r, g, b }) => {
    r /= 255
    g /= 255
    b /= 255
    const max = Math.max(r, g, b),
        min = Math.min(r, g, b)
    let h = 0,
        s = 0,
        l = (max + min) / 2
    if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g:
                h = (b - r) / d + 2
                break
            case b:
                h = (r - g) / d + 4
                break
        }
        h /= 6
    }
    return { h: h * 360, s, l }
}

export const hslToRgb = ({ h, s, l }) => {
    h /= 360
    let r, g, b
    if (s === 0) {
        r = g = b = l
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1
            if (t > 1) t -= 1
            if (t < 1 / 6) return p + (q - p) * 6 * t
            if (t < 1 / 2) return q
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
            return p
        }
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        r = hue2rgb(p, q, h + 1 / 3)
        g = hue2rgb(p, q, h)
        b = hue2rgb(p, q, h - 1 / 3)
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255),
    }
}

export const rgbToHex = ({ r, g, b }) => {
    const toHex = (x) => x.toString(16).padStart(2, "0")
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export const createColorVariation = (baseColor, lightPercent, hueShift) => {
    // Convert base color to HSL
    const rgb = hexToRgb(baseColor)
    const hsl = rgbToHsl(rgb)
    // Apply lightening and hue shift
    const newH = (hsl.h + hueShift + 360) % 360
    const newL = Math.min(Math.max(hsl.l + lightPercent, 0), 1)
    // Convert back to hex
    const newRgb = hslToRgb({ h: newH, s: hsl.s, l: newL })
    return rgbToHex(newRgb)
}

// Historical single calendar — the universal fallback used whenever a channel
// URL isn't (yet) configured in backend static data. Keeps every "Prendre RDV"
// CTA pointing at a live calendar during the Calendly channel split.
const CALENDLY_URL_FALLBACK = "https://masteur.com/calendly"

// "Prendre RDV" context → audience channel. The 3 channel base URLs come from
// backend static data (state.data.staticData.calendly_urls), so ops can swap a
// calendar without an app redeploy.
const CALENDLY_CHANNEL_BY_TYPE = {
    "trial-done": "leads",
    "fast-track": "leads",
    fsm: "support",
    cancel: "resiliation",
}

/**
 * Resolve the Calendly base URL for a given context type, reading the
 * centralized channel URLs from static data and falling back to the historical
 * single calendar when a channel isn't configured.
 */
export const getCalendlyBaseUrl = (type = "trial-done") => {
    const channel = CALENDLY_CHANNEL_BY_TYPE[type] ?? "leads"

    let channelUrls
    try {
        // Lazy require avoids a module-init cycle (a reducer imports this file).
        const { store } = require("@data/redux/storeConfig/store")
        channelUrls = store.getState()?.data?.staticData?.calendly_urls
    } catch (e) {
        channelUrls = null
    }

    return channelUrls?.[channel] || CALENDLY_URL_FALLBACK
}

/**
 * Take a date string in the format "YYYY-MM-DD HH:MM" and return a date string in the format "YYYYMMDDTHHmmss"
 */
export const formatDateForGoogleCalendar = (dateString, addMinutes = 0) => {
    const date = new Date(dateString)
    return moment(date)
        .add(addMinutes, "minutes")
        .toISOString()
        .replace(/[^\w\s]/gi, "")
}

export const openCalendly = (student, type = "trial-done", customCallInfo = null) => {
    let calendlyUrl = getCalendlyBaseUrl(type)

    const callInfo = customCallInfo
        ?? (type === "trial-done"
            ? "Suite à la séance découverte"
            : type === "fast-track"
                ? "Demande de renseignements"
                : type === "fsm"
                    ? "Question concernant l'accompagnement"
                    : null)

    if (student) {
        const params = new URLSearchParams()

        const firstName = student.parent_firstname ?? student.firstname
        if (firstName) params.append("first_name", firstName)

        const lastName = student.parent_lastname ?? student.lastname
        if (lastName) params.append("last_name", lastName)

        const email = student.parent_email ?? student.email
        if (email) params.append("email", email)

        if (student.parent_mobile) {
            params.append("location", student.parent_mobile)
            params.append("a1", student.parent_mobile)
        }

        if (callInfo) params.append("a2", callInfo)

        const queryString = params.toString()
        if (queryString) {
            calendlyUrl = `${calendlyUrl}?${queryString}`
        }
    }

    if (IS_WEB) {
        window.open(calendlyUrl, "_blank")
    } else {
        WebBrowser.openBrowserAsync(calendlyUrl)
    }
}

export const formatAvailabilities = (availabilities) => {
    if (!availabilities || !Array.isArray(availabilities)) return []

    const dayMapping = {
        Monday: "Lun",
        Tuesday: "Mar",
        Wednesday: "Mer",
        Thursday: "Jeu",
        Friday: "Ven",
        Saturday: "Sam",
        Sunday: "Dim",
    }

    const toMinutes = (t) => {
        const [h, m] = t.split(":").map((x) => parseInt(x, 10))
        return h * 60 + (m || 0)
    }

    const minutesToTimeLabel = (mins) => {
        const hour = Math.floor(mins / 60)
        const minute = mins % 60
        if (minute === 0) {
            return `${hour}`
        } else {
            return `${hour}:${minute.toString().padStart(2, "0")}`
        }
    }

    const roundDownToHour = (mins) => mins - (mins % 60)

    // Group times by day key
    const groupedByDay = availabilities.reduce((acc, slot) => {
        const [day, time] = slot.split(" ")
        const key = dayMapping[day] || day
        if (!acc[key]) acc[key] = []
        acc[key].push(time)
        return acc
    }, {})

    const availabilityItems = []
    Object.entries(groupedByDay).forEach(([day, times]) => {
        const sortedMins = times.map(toMinutes).sort((a, b) => a - b)

        // Build continuous 30-min blocks
        const blocks = []
        let blockStart = sortedMins[0]
        let prev = sortedMins[0]
        for (let i = 1; i < sortedMins.length; i += 1) {
            const current = sortedMins[i]
            if (current - prev === 30) {
                prev = current
                continue
            }
            blocks.push([blockStart, prev + 30])
            blockStart = current
            prev = current
        }
        blocks.push([blockStart, prev + 30])

        // Format blocks as individual items
        blocks.forEach(([startMin, endMin]) => {
            const startTime = minutesToTimeLabel(roundDownToHour(startMin))
            // endMin is already 30 minutes after the last slot, so add 30 more to get 1 hour after
            const endTime = minutesToTimeLabel(endMin + 30)
            availabilityItems.push(`${day} ${startTime}-${endTime}`)
        })
    })

    return availabilityItems
}

export const getOriginalSlotsForFormattedAvailability = (
    studentAvailabilities,
    formattedAvailability
) => {
    const dayMapping = {
        Monday: "Lun",
        Tuesday: "Mar",
        Wednesday: "Mer",
        Thursday: "Jeu",
        Friday: "Ven",
        Saturday: "Sam",
    }

    // Parse the formatted availability (e.g., "Sam 10-12")
    const [dayShort, timeRange] = formattedAvailability.split(" ")
    const [startHour, endHour] = timeRange.split("-").map((h) => parseInt(h))

    // Find the original day name
    const originalDay = Object.keys(dayMapping).find(
        (key) => dayMapping[key] === dayShort
    )

    if (!originalDay) return []

    // Generate all 30-minute slots in the range
    const slots = []
    for (let hour = startHour; hour < endHour; hour++) {
        slots.push(`${originalDay} ${hour.toString().padStart(2, "0")}:00`)
        slots.push(`${originalDay} ${hour.toString().padStart(2, "0")}:30`)
    }

    // Filter to only include slots that actually exist in student's availabilities
    return slots.filter((slot) => studentAvailabilities.includes(slot))
}

// ** School subjects filtering utilities
const schoolClassOrder = studentClasses

const getClassRank = (cls) => {
    const i = schoolClassOrder.indexOf(cls)
    return i >= 0 ? i : schoolClassOrder.indexOf("Autre")
}

export const filterSchoolSubjectsByClass = (schoolSubjects, studentClass) => {
    if (!studentClass) return schoolSubjects || []
    return (schoolSubjects || []).filter((opt) => {
        const minCls = opt?.min_student_class
        if (!minCls) return true
        return getClassRank(studentClass) >= getClassRank(minCls)
    })
}

/**
 * Expand squashed time slots to individual hourly slots
 * Example: ["Monday 18-20"] → ["Monday 18h-19h", "Monday 19h-20h"]
 */
export const expandSlots = (squashedSlots) => {
    if (!squashedSlots || !Array.isArray(squashedSlots)) return []

    const expandedSlots = []
    squashedSlots.forEach((slot) => {
        const [day, timeRange] = slot.split(" ")
        if (!timeRange || !timeRange.includes("-")) return

        const [startHour, endHour] = timeRange
            .split("-")
            .map((h) => parseInt(h))
        if (isNaN(startHour) || isNaN(endHour)) return

        // Generate individual hourly slots
        for (let hour = startHour; hour < endHour; hour++) {
            const paddedHour = hour.toString().padStart(2, "0")
            const paddedNextHour = (hour + 1).toString().padStart(2, "0")
            expandedSlots.push(`${day} ${paddedHour}h-${paddedNextHour}h`)
        }
    })
    return expandedSlots
}

/**
 * Squash individual hourly slots into time ranges
 * Example: ["Monday 18h-19h", "Monday 19h-20h", "Monday 20h-21h"] → ["Monday 18h-21h"]
 */
export const squashSlots = (individualSlots) => {
    if (!individualSlots || !Array.isArray(individualSlots)) return []

    // Group slots by day
    const slotsByDay = {}
    individualSlots.forEach((slot) => {
        const [day, timeRange] = slot.split(" ")
        if (!timeRange || !timeRange.includes("-")) return

        const startHour = parseInt(timeRange.split("h-")[0])
        if (isNaN(startHour)) return

        if (!slotsByDay[day]) {
            slotsByDay[day] = []
        }
        slotsByDay[day].push(startHour)
    })

    // Squash consecutive hours for each day
    const squashedSlots = []
    Object.keys(slotsByDay).forEach((day) => {
        const hours = slotsByDay[day].sort((a, b) => a - b)
        let rangeStart = hours[0]
        let rangeEnd = hours[0] + 1

        for (let i = 1; i < hours.length; i++) {
            if (hours[i] === rangeEnd) {
                // Consecutive hour, extend the range
                rangeEnd = hours[i] + 1
            } else {
                // Gap detected, save current range and start new one
                const paddedStart = rangeStart.toString().padStart(2, "0")
                const paddedEnd = rangeEnd.toString().padStart(2, "0")
                squashedSlots.push(`${day} ${paddedStart}h-${paddedEnd}h`)
                rangeStart = hours[i]
                rangeEnd = hours[i] + 1
            }
        }
        // Save the last range
        const paddedStart = rangeStart.toString().padStart(2, "0")
        const paddedEnd = rangeEnd.toString().padStart(2, "0")
        squashedSlots.push(`${day} ${paddedStart}h-${paddedEnd}h`)
    })

    return squashedSlots
}
